import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { combineLatest, Subject, takeUntil } from "rxjs";
import { selectCheckInfo, selectStagedTransactionForm } from "../../../../../../create-request/reducers";
import { amountTypeDict, checkPayeeDict, CheckPayeeTypes, Constants, Frequencies, frequencyDict, FrequencyTypes, payIncomeDict, PayIncomeType, transactionDetailLabels, transactionTypes, transactionTypesDict, yesNo } from "projects/money-movement-ui/src/app/shared/utilities/Constants";
import { ChecksExtraPrompts, CheckTransactionDetails, isCheckTransactionForm, TransactionForm } from "../../../../../../create-request/models/form/TransactionForm";
import { AmountType } from "projects/money-movement-ui/src/app/shared/types/Transaction";
import { CurrencyPipe } from "@angular/common";
import { SentenceCasePipe } from "projects/money-movement-ui/src/app/shared/pipes/sentence-case.pipe";
import { CodeAndDescription } from "../../../../../../create-request/models/shared/CodeAndDescription";
import { selectInitReleaseTransactionInContext, selectInitReleaseTransactionsByDraftIdCallSuccess } from "projects/money-movement-ui/src/app/initiate-and-release/reducers";
import { FormatDatePipe } from "projects/money-movement-ui/src/app/shared/pipes/FormatDatePipe";
import { selectIsTransactionsViewPage } from "projects/money-movement-ui/src/app/transaction-view/reducers";
import { selectFilteredChecksStandingAuth, selectIsPendingCheckSloaSetup } from "projects/money-movement-ui/src/app/account-info-header/reducers";
import { selectIsEnableRecurringChecksStandingAuth } from "projects/money-movement-ui/src/app/feature-flags/reducers";
import { StandingAuthDetails } from "projects/money-movement-ui/src/app/shared/types/StandingInstruction";

@Component({
  standalone: false,
    selector: 'mm-transaction-details-check-condensed',
    templateUrl: './transaction-details-check-condensed.component.html',
    styleUrls: ['./transaction-details-check-condensed.component.scss']
})

export class TransactionDetailsCheckCondensedComponent implements OnInit, OnDestroy {
    readonly labels = transactionDetailLabels;
    readonly processDateCopy = Constants.checkProcessDateReviewCopy;
    readonly sectionTitle: string = 'Transaction details';
    readonly free: string = 'Free';
    readonly bundled: string = 'bundled';
    readonly currencyPipe = new CurrencyPipe('en-US', 'USD');
    readonly sentenceCasePipe = new SentenceCasePipe;
    readonly onetimeTotalDist: string = 'onetimeTotalDist';
    @Input() isInitiateAndReleaseFlowFromTable: boolean;
    isIraTransaction: boolean;
    transactionType: string = transactionTypesDict.check;
    useStandingAuth: string;
    frequency: string;
    payee: string;
    deliveryMethod: string;
    checkReason: string;
    amount: string; 
    grossNet: string;
    netCashAmount: string;
    processDate: string;
    isRecurringTransaction: boolean;
    frequencyOfRecurringTransfer: string;
    secondProcessDate: string;
    endDate: string;
    payIncome: string;
    showPayIncome: boolean;

    destroy$: Subject<void> = new Subject<void>();
    isOneTimeTotalDist: boolean;
    closeAccount: string;
    reportsRequested: string[] = [];
    reportsRequestedTooltip: string = Constants.reportsRequestedTooltip;
    totalWithdrawalAmount: string;
    standingAuthDetails: StandingAuthDetails | null = null;
    isTransactionViewPage: boolean;
    isBsloaTransaction: boolean;
    instructionId: string;
    isPendingSloaSetup: boolean;
    isBundledOneTimeTotalDist: boolean;
    checkNumber: string;
    transactionStatus: string;
    enableRecurringChecksStandingAuth: boolean;
    standingAuthNicknameInstructionId: string = '';
    nextProcessDate: string = '';

    constructor(private store: Store, private dateFormatPipe: FormatDatePipe) {}

    ngOnInit(): void {
        this.store.select(selectIsTransactionsViewPage).pipe(takeUntil(this.destroy$)).subscribe(value => this.isTransactionViewPage = value);
        this.store.select(selectIsEnableRecurringChecksStandingAuth).pipe(takeUntil(this.destroy$)).subscribe(enableRecurringChecksStandingAuth => {
            this.enableRecurringChecksStandingAuth = enableRecurringChecksStandingAuth;
        });
        combineLatest([
            this.store.select(selectStagedTransactionForm),
            this.store.select(selectCheckInfo),
            this.store.select(selectInitReleaseTransactionInContext),
            this.store.select(selectInitReleaseTransactionsByDraftIdCallSuccess)
        ]).pipe(takeUntil(this.destroy$)).subscribe(([form, info, inr, draftCallSuccess]) => {
            let details = form.transactionDetails as CheckTransactionDetails;
            let extraPrompts = form.extraPrompts as ChecksExtraPrompts;
            let isTableEditFlow = form.prefilledFlow.name === 'edit-table' && form.prefilledFlow.state === 'complete';
            this.isBsloaTransaction = extraPrompts.useStandingAuth === this.bundled;
            this.isIraTransaction = info.iraAccount;
            this.transactionType = this.isBsloaTransaction ? `${transactionTypesDict[transactionTypes.check]} - bundled` : transactionTypesDict[transactionTypes.check];
            this.useStandingAuth = this.getUseStandingAuth(this.isBsloaTransaction, extraPrompts);
            this.closeAccount = extraPrompts?.closeAccount ? extraPrompts?.closeAccount?.charAt(0).toUpperCase() + extraPrompts?.closeAccount?.slice(1) : '';
            this.frequency = this.getFrequency(extraPrompts, this.getFrequencyType(details, extraPrompts));
            this.isOneTimeTotalDist = this.frequency === frequencyDict['onetimeTotalDist'];
            this.isBundledOneTimeTotalDist = this.isBsloaTransaction && extraPrompts?.useBundledStandingAuth === this.onetimeTotalDist;
            this.isRecurringTransaction = (this.frequency === (frequencyDict['recurring'] as FrequencyTypes));
            this.payee = this.getPayee(details, info.isThirdParty);
            this.deliveryMethod = this.getDeliveryMethod(info.checkFee, details.deliveryMethod ?? '');
            this.checkReason = info.checkReasonMap?.find(element => element.code === details.checkReason!)?.description ?? inr?.draftObject?.withdrawalReason ?? '';
            this.amount = this.getAmountByAmountType(details, info.totalWithdrawalAmount);
            this.grossNet = details.amountType ?? '';
            this.netCashAmount = info.amount ?? Constants.zeroAmount;
            this.processDate = info.processDate;
            this.frequencyOfRecurringTransfer = this.getFrequencyOfRecurringTransfer(info.frequencyList, details.recurringFrequency ?? '', details.frequencyDescription ?? '');
            this.secondProcessDate = details.secondProcessDate ?? '';
            this.endDate = details.endDate ?? '';
            this.payIncome = details?.payIncome ? payIncomeDict[details.payIncome] as PayIncomeType : '' as PayIncomeType;
            this.showPayIncome = details.recurringFrequency === Constants.checkLastBizDay && !!this.payIncome;
            this.formatReportsRequestedFields(form);
            this.totalWithdrawalAmount = this.isOneTimeTotalDist ? info?.totalWithdrawalAmount : '';
            this.nextProcessDate = info.cashieringCheckInputDetails.nextProcessDate ?? '';
            this.checkNumber = info.checkNumber?.toString() ?? '0';
            this.transactionStatus = info.status?.description ?? '';
            this.instructionId = extraPrompts.standingAuthId?.includes('-') ? (extraPrompts.standingAuthId?.split('-')[1]) : extraPrompts.standingAuthId ? extraPrompts.standingAuthId : '';
            if ((this.isTransactionViewPage || isTableEditFlow || draftCallSuccess) && this.useStandingAuth === 'yes') {
                this.standingAuthDetails = {
                    nickname: this.resolveStandingAuthNickname(info?.nickName),
                    payeeLines: [
                        info.payeeDetails.payeeName1,
                        info.payeeDetails.payeeName2,
                        info.payeeDetails.payeeName3
                    ],
                    addressLines: [
                        `${info.recipient.addressLine1} ${info.recipient.addressLine2} ${info.recipient.city}, ${info.recipient.stateCode}`,
                        `${info.recipient.zipCode} ${info.recipient.countryCode}`
                    ]
                };
            }
        });
        this.store.select(selectIsPendingCheckSloaSetup).pipe(takeUntil(this.destroy$)).subscribe(isPendingSloaSetup => {
            this.isPendingSloaSetup = isPendingSloaSetup;
        });
        this.store.select(selectFilteredChecksStandingAuth).pipe(takeUntil(this.destroy$)).subscribe(instructions => {
            let selectedStandingAuthorization = instructions.find(auths => auths.cashieringRqstId === this.instructionId);
            if (selectedStandingAuthorization) {
                this.standingAuthDetails = {
                    nickname: this.resolveStandingAuthNickname(selectedStandingAuthorization.nickname),
                    payeeLines: [
                        selectedStandingAuthorization.to1,
                        selectedStandingAuthorization.to2,
                        selectedStandingAuthorization.to3
                    ],
                    addressLines: [selectedStandingAuthorization.address]
                };
            }
        });
    }

    get standingAuthDetailsLines(): string[] {
        if (!this.standingAuthDetails) {
            return [];
        }
        return [
            this.standingAuthDetails.nickname,
            ...this.standingAuthDetails.payeeLines,
            ...this.standingAuthDetails.addressLines
        ].filter(line => !!line?.trim());
    }

    resolveStandingAuthNickname(incomingNickname?: string): string {
        const normalizedIncomingNickname = incomingNickname?.trim() ?? '';
        if (normalizedIncomingNickname) {
            this.standingAuthNicknameInstructionId = this.instructionId ?? '';
            return normalizedIncomingNickname;
        }

        if (this.standingAuthNicknameInstructionId === (this.instructionId ?? '')) {
            return this.standingAuthDetails?.nickname ?? '';
        }

        return '';
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getUseStandingAuth(isBsloaTransaction: boolean, extraPrompts: ChecksExtraPrompts): string {
        let useStandingAuthVal: string = extraPrompts?.useStandingAuth ?? '';
        if (isBsloaTransaction) {
            if (extraPrompts?.useBundledStandingAuth === Frequencies.recurring) {
                useStandingAuthVal = yesNo.no;
            } else if (extraPrompts?.useBundledStandingAuth === this.onetimeTotalDist) {
                useStandingAuthVal = yesNo.no;
            } else {
                useStandingAuthVal = extraPrompts?.useBundledStandingAuth ?? '';
            }
        }
        return useStandingAuthVal;
    }

    getFrequency(extraPrompts: ChecksExtraPrompts, frequency: string): string {
        if (extraPrompts.useStandingAuth === this.bundled){
            if (extraPrompts.useBundledStandingAuth === Frequencies.recurring || (extraPrompts.useBundledStandingAuth === yesNo.yes && frequency === Frequencies.recurring)) {
                return frequencyDict.recurring;
            }
            else if (extraPrompts.useBundledStandingAuth === yesNo.no || (extraPrompts.useBundledStandingAuth === yesNo.yes && (frequency === Frequencies.onetime || frequency === Frequencies.onRequest))) {
                return frequencyDict.onetime;
            }
            else if (extraPrompts.useBundledStandingAuth === this.onetimeTotalDist || (extraPrompts.useBundledStandingAuth === yesNo.yes && frequency === this.onetimeTotalDist)) {
                return frequencyDict.onetimeTotalDist;
            }
            else {
                return '';
            }
        }
        else if (extraPrompts.useStandingAuth === yesNo.yes && !!frequency) {
            if (frequency === Frequencies.onetime || frequency === Frequencies.onRequest) {
                return frequencyDict.onetime;
            } else if (frequency === this.onetimeTotalDist) {
                return frequencyDict.onetimeTotalDist;
            } else {
                return frequencyDict.recurring;
            }
        }
        return frequencyDict[extraPrompts.frequency!] as FrequencyTypes;
    }

    getPayee(transactionDetails: CheckTransactionDetails, isThirdParty: boolean): CheckPayeeTypes {
        if (this.useStandingAuth === yesNo.no) {
          return checkPayeeDict[transactionDetails.payeeType!] as CheckPayeeTypes;
        }
        return isThirdParty ? checkPayeeDict.thirdParty as CheckPayeeTypes : checkPayeeDict.firstParty as CheckPayeeTypes;
    }

    getDeliveryMethod(checkFee: number, deliveryMethod: string): string {
        let deliveryFee = checkFee === 0 ? this.free : this.currencyPipe.transform(checkFee);
        return `${this.sentenceCasePipe.transform(deliveryMethod)}—${deliveryMethod.toLowerCase() === 'standard' ? this.free : deliveryFee}`;
    }

    getAmountByAmountType(transactionDetails: CheckTransactionDetails, totalWithdrawalAmount: string): string {
        if (transactionDetails.amountType === (amountTypeDict['net'] as AmountType)) {
          return (totalWithdrawalAmount ?? Constants.zeroAmount);
        }
        return transactionDetails.amount === '' || transactionDetails.amount === null || transactionDetails.amount === undefined
            ? Constants.zeroAmount
            : transactionDetails.amount;
    }

    getFrequencyOfRecurringTransfer(frequencyList: CodeAndDescription[], recurringFrequency: string, frequencyDescription: string): string {
        // use recurringFrequency for review/confirmation pages as this is the value set by user in input step
        if (frequencyDescription && (frequencyDescription === frequencyList?.find((f: CodeAndDescription) => f.code === recurringFrequency)?.description || this.isInitiateAndReleaseFlowFromTable)) {
            return frequencyDescription;
        }
        else {
            return frequencyList?.find((f: CodeAndDescription) => f.code === recurringFrequency)?.description ?? '';
        }
    }

    getFrequencyType(details: any, extraPrompts: any) {
        // use frequency from transaction details for review/confirmation pages as value is set by user in input step
        // use frequency from extraPrompts when user view the details from table
        // use recurringFrequency from transaction details when standing Auth is yes and user view the details from table
        let isSloaOrBsloa = extraPrompts?.useStandingAuth === 'yes' || (extraPrompts?.useStandingAuth === this.bundled && extraPrompts?.useBundledStandingAuth === yesNo.yes);
        if (isSloaOrBsloa && extraPrompts?.frequency) {
            return extraPrompts.frequency;
        }
        return details.frequency ?? extraPrompts.frequency ?? details.recurringFrequency;
    }

    get hasCheckNumberForCompletedTransaction() : boolean {
        return this.isTransactionViewPage && this.transactionStatus.toLowerCase() === 'complete' && this.checkNumber !== '' && this.checkNumber !== '0';
    }

    formatReportsRequestedFields(form: TransactionForm) {
    if (form.transactionType && isCheckTransactionForm(form)) {
        const fields = [
        { label: 'Tax forms through: ', value: form?.extraPrompts?.taxFormsDateInputControl },
        { label: 'Account statements through: ', value: this.dateFormatPipe.transform(form?.extraPrompts?.accountStatementsDateInputControl ?? '') },
        { label: 'Trade confirmations through: ', value: this.dateFormatPipe.transform(form?.extraPrompts?.tradeConfirmationsDateInputControl ?? '') },
        ];
        fields.forEach(field => {
        if (field.value) {
            this.reportsRequested.push(field.label, field.value);
        }
        });
    }
    }

    shouldShowStandingAuthFields(): boolean {
        const isStandingAuthTransaction = this.useStandingAuth === yesNo.yes;

        // Details are shown only when standing auth resolves to yes and setup isn't pending.
        if (!isStandingAuthTransaction || this.isPendingSloaSetup) {
            return false;
        }
        
        // Recurring standing auth details are feature-flag gated.
        if (this.isRecurringTransaction) {
            return this.enableRecurringChecksStandingAuth;
        }
        
        // One-time standing auth details are always shown.
        return true;
    }
}